import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, CheckCircle, Calendar } from 'lucide-react';
import { seriesApi, companyApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const AddEpisodeDialog = ({ open, onOpenChange, seriesId, seriesTitle, seasonNumber, existingEpisodeCount = 0, onEpisodeAdded }) => {
  const nextEp = existingEpisodeCount + 1;
  const [form, setForm] = useState({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState(null);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const thumbRef = useRef(null);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStep(null);
    try {
      const result = await companyApi.uploadVideo(file, (pct, step) => {
        setUploadProgress(pct);
        if (step) setUploadStep(step);
      });
      setForm(p => ({ ...p, video_file: result.url, videoFileName: file.name, duration: result.duration || 0 }));
      toast.success('Video caricato');
    } catch (err) { toast.error(err.message || 'Upload fallito'); }
    finally { setUploading(false); setUploadProgress(0); setUploadStep(null); }
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const preview = URL.createObjectURL(file);
      setForm(p => ({ ...p, thumbnailPreview: preview }));
      const result = await companyApi.uploadThumbnail(file);
      setForm(p => ({ ...p, thumbnail: result.url }));
      toast.success('Thumbnail caricata');
    } catch (err) { toast.error(err.message || 'Upload fallito'); }
    finally { setUploadingThumb(false); }
  };

  const handleSubmit = async () => {
    if (!form.title) { toast.error('Il titolo è obbligatorio'); return; }
    if (!form.video_file) { toast.error('Carica un video per l\'episodio'); return; }
    setSubmitting(true);
    try {
      const result = await seriesApi.addEpisode(seriesId, {
        season_number: seasonNumber,
        title: form.title,
        description: form.description,
        video_file: form.video_file,
        thumbnail: form.thumbnail,
        duration: form.duration || 0,
        release_date: form.release_date || undefined,
      });
      toast.success(`Episodio ${result.episode_number || nextEp} aggiunto alla Stagione ${seasonNumber}! In attesa di approvazione.`);
      onEpisodeAdded?.();
      onOpenChange(false);
      setForm({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' });
    } catch (err) { toast.error(err.message || 'Errore nell\'aggiunta dell\'episodio'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col bg-[#0F0B1E] border-white/10 p-0" data-testid="add-episode-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 shrink-0">
          <DialogTitle className="text-lg font-bold">Aggiungi Episodio alla Stagione {seasonNumber}</DialogTitle>
          <p className="text-sm text-gray-400 mt-1">"{seriesTitle}" &mdash; Episodio {nextEp}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" style={{ maxHeight: '55vh' }}>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Titolo *</label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Titolo episodio" className="bg-[#030014] border-[#8B5CF6]/20 h-9" data-testid="add-ep-title" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Descrizione</label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Descrizione episodio" className="bg-[#030014] border-[#8B5CF6]/20 min-h-[60px]" data-testid="add-ep-desc" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Video *</label>
            {form.video_file ? (
              <div className="flex items-center gap-2 p-2 bg-[#00FF9D]/10 border border-[#00FF9D]/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-[#00FF9D]" />
                <span className="text-xs text-[#00FF9D] flex-1 truncate">{form.videoFileName}</span>
                <button onClick={() => setForm(p => ({ ...p, video_file: '', videoFileName: '' }))} className="p-0.5"><X className="w-3 h-3 text-gray-400" /></button>
              </div>
            ) : uploading ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-[#00FF9D] animate-spin" />
                  <span className="text-xs">
                    {uploadStep && uploadStep !== 'queued'
                      ? `Ottimizzazione... (${
                          { downloading: 'download', analyzing: 'analisi',
                            transcoding: 'transcodifica', faststart: 'ottimizzazione',
                            uploading: 'finalizzazione', done: 'fatto' }[uploadStep] || uploadStep
                        })`
                      : uploadProgress >= 96
                        ? 'Ottimizzazione per lo streaming...'
                        : `${uploadProgress}%`}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-gray-800 rounded-full"><div className="h-full bg-[#00FF9D] rounded-full transition-all" style={{ width: `${uploadProgress}%` }} /></div>
              </div>
            ) : (
              <>
                <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
                <Button type="button" variant="outline" size="sm" onClick={() => videoRef.current?.click()} className="border-[#00FF9D]/30 text-[#00FF9D]" data-testid="add-ep-video-btn">
                  <Upload className="w-3 h-3 mr-1" /> Carica Video
                </Button>
              </>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Thumbnail (opzionale)</label>
            {form.thumbnailPreview && <img src={form.thumbnailPreview} alt="" className="w-32 h-20 object-cover rounded mb-1 border border-gray-800" />}
            <input ref={thumbRef} type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} />
            <Button type="button" variant="outline" size="sm" onClick={() => thumbRef.current?.click()} disabled={uploadingThumb} className="border-gray-700">
              {uploadingThumb ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />} Thumbnail
            </Button>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Data di rilascio <span className="text-gray-600">(opzionale)</span></label>
            <Input type="date" value={form.release_date} onChange={e => setForm(p => ({ ...p, release_date: e.target.value }))}
              min={new Date().toISOString().split('T')[0]} className="bg-[#030014] border-[#8B5CF6]/20 h-8 max-w-[160px] text-sm" data-testid="add-ep-date" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3 justify-end shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting || !form.title || !form.video_file} className="btn-primary" data-testid="submit-episode-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Aggiungi Episodio
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEpisodeDialog;
